import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Shield, Users, CheckCircle, XCircle, RefreshCw, Edit2, Save, X, AlertCircle } from "lucide-react";
import axios from "axios";
import Layout from "@/components/Layout";

// All available pages in the system
const ALL_PAGES = [
  '/dashboard',
  '/dashboard-estimation',
  '/projects',
  '/project-planning',
  '/my-workspace',
  '/resource-allocation',
  '/rfi-log',
  '/transmittal-log',
  '/change-order-log',
  '/estimations',
  '/wbs-estimations',
  '/clients',
  '/leads',
  '/lead-approvals',
  '/invoices',
  '/timesheets',
  '/timesheet-calendar',
  '/timesheet-reports',
  '/leave-management',
  '/leave-approvals',
  '/hr/leave-allocations',
  '/hr/holidays',
  '/settings/wbs-master-list',
  '/users',
  '/employees',
  '/interns',
  '/teams',
  '/vendors',
  '/reports',
  '/sales',
  '/error-tracking',
  '/error-settings',
  '/webhooks',
  '/company-settings',
  '/rbac',
  // Performance & Awards Module
  '/performance/kra-settings',
  '/performance/employee-targets',
  '/performance/summary',
  '/performance/tracking',
  '/performance/reviews',
  '/performance/awards',
  '/performance/reports',
  '/performance/my-performance'
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RBACViewer({ user }) {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editedPermissions, setEditedPermissions] = useState(null);
  const [editedDefaultPage, setEditedDefaultPage] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/rbac/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error("Failed to fetch roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleDetails = async (roleId) => {
    setLoading(true);
    setIsEditMode(false); // Exit edit mode when switching roles
    setEditedPermissions(null);
    setSaveMessage(null);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/rbac/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure all pages are included in the response
      const roleData = response.data;
      const pages = roleData.pages || {};
      
      // Add missing pages with all permissions set to false
      ALL_PAGES.forEach(page => {
        if (!pages[page]) {
          pages[page] = {
            view: false,
            create: false,
            edit: false,
            delete: false,
            export: false
          };
        }
      });
      
      roleData.pages = pages;
      setSelectedRole(roleData);
    } catch (error) {
      console.error("Failed to fetch role details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterEditMode = () => {
    if (selectedRole) {
      setEditedPermissions(JSON.parse(JSON.stringify(selectedRole.pages))); // Deep copy
      setEditedDefaultPage(selectedRole.default_page || '/dashboard');
      setIsEditMode(true);
      setSaveMessage(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedPermissions(null);
    setEditedDefaultPage(null);
    setSaveMessage(null);
  };

  const handleTogglePermission = (page, action) => {
    if (!isEditMode || !editedPermissions) return;
    
    setEditedPermissions(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        [action]: !prev[page][action]
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedRole || !editedPermissions) return;
    
    setSaving(true);
    setSaveMessage(null);
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/rbac/roles/${selectedRole.role_id}`,
        {
          pages: editedPermissions,
          default_page: editedDefaultPage
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSaveMessage({ type: 'success', text: 'Permissions and default page saved successfully!' });
      setIsEditMode(false);
      
      // Refresh role details
      await fetchRoleDetails(selectedRole.role_id);
      
      // Refresh roles list to update user counts if needed
      await fetchRoles();
    } catch (error) {
      console.error("Failed to save permissions:", error);
      setSaveMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to save permissions. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMigrateUsers = async () => {
    if (!window.confirm("Are you sure you want to migrate all users to the new RBAC system?")) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/users/migrate-to-rbac`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMigrationResult(response.data);
      fetchRoles(); // Refresh roles to get updated user counts
    } catch (error) {
      console.error("Failed to migrate users:", error);
      alert("Migration failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentColor = (dept) => {
    const colors = {
      'Management': 'bg-purple-100 text-purple-800',
      'Sales': 'bg-blue-100 text-blue-800',
      'Estimation': 'bg-green-100 text-green-800',
      'Projects': 'bg-yellow-100 text-yellow-800',
      'Pmo': 'bg-orange-100 text-orange-800',
      'Human Resources': 'bg-pink-100 text-pink-800',
      'Accounts And Finance': 'bg-indigo-100 text-indigo-800',
      'Training': 'bg-teal-100 text-teal-800',
      'Quality Assurance': 'bg-red-100 text-red-800'
    };
    return colors[dept] || 'bg-md3-surface-container text-md3-on-surface';
  };

  const getLevelColor = (level) => {
    const colors = {
      'Admin': 'bg-red-500 text-white',
      'Manager': 'bg-orange-500 text-white',
      'Team Lead': 'bg-blue-500 text-white',
      'Team Member': 'bg-green-500 text-white'
    };
    return colors[level] || 'bg-gray-500 text-white';
  };

  return (
    <Layout user={user}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-md3-display-small flex items-center gap-2">
              <Shield className="h-8 w-8 text-purple-600" />
              Role & Permissions Management
            </h1>
            <p className="text-md3-on-surface-variant mt-2">
              View all roles, manage permissions, and control user access across the system
            </p>
          </div>
          
          {user?.department === 'Management' && user?.access_level === 'Admin' && (
            <Button onClick={handleMigrateUsers} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Migrate All Users to RBAC
            </Button>
          )}
        </div>

        {/* Current User Info */}
        <Card className="mb-6 border-purple-200 bg-purple-50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Your Current Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Department</p>
                <Badge className={getDepartmentColor(user?.department)}>
                  {user?.department}
                </Badge>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Access Level</p>
                <Badge className={getLevelColor(user?.access_level)}>
                  {user?.access_level}
                </Badge>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Role ID</p>
                <p className="font-mono font-bold text-md3-body-small">{user?.role_id}</p>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">System</p>
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  RBAC Active
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Migration Result */}
        {migrationResult && (
          <Card className="mb-6 border-green-200 bg-green-50 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
            <CardHeader>
              <CardTitle className="text-green-800 text-md3-title-medium text-md3-on-surface">Migration Complete</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-md3-headline-large text-green-600">{migrationResult.updated_count}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant">Updated</p>
                </div>
                <div>
                  <p className="text-md3-headline-large text-blue-600">{migrationResult.skipped_count}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant">Skipped</p>
                </div>
                <div>
                  <p className="text-md3-headline-large text-purple-600">{migrationResult.total_users}</p>
                  <p className="text-md3-body-small text-md3-on-surface-variant">Total Users</p>
                </div>
              </div>
              <p className="text-md3-body-small text-green-700 mt-4">{migrationResult.message}</p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Roles List */}
          <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardHeader>
              <CardTitle className="text-md3-title-large text-md3-on-surface ">All Roles ({roles.length})</CardTitle>
              <CardDescription>
                Click on a role to view detailed permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {roles.map((role) => (
                  <div
                    key={role.role_id}
                    onClick={() => fetchRoleDetails(role.role_id)}
                    className={`p-3 border rounded-md3-lg cursor-pointer transition-all hover:shadow-md ${
                      selectedRole?.role_id === role.role_id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-md3-outline-variant hover:border-purple-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={getDepartmentColor(role.department)}>
                            {role.department}
                          </Badge>
                          <Badge className={getLevelColor(role.access_level)}>
                            {role.access_level}
                          </Badge>
                        </div>
                        <p className="text-xs text-md3-on-surface-variant mt-1">{role.description}</p>
                        <div className="flex gap-3 mt-2 text-xs text-md3-on-surface-variant">
                          <span>📄 {role.page_count} pages</span>
                          <span>👥 {role.user_count} users</span>
                        </div>
                      </div>
                      {role.all_access && (
                        <Badge className="bg-red-500 text-white">FULL ACCESS</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Role Details */}
          <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-md3-title-large text-md3-on-surface ">
                    {selectedRole ? 'Role Permissions' : 'Select a Role'}
                  </CardTitle>
                  <CardDescription>
                    {selectedRole 
                      ? `${isEditMode ? 'Editing' : 'Viewing'} permissions for ${selectedRole.role_id}`
                      : 'Click a role from the list to view its permissions'
                    }
                  </CardDescription>
                </div>
                
                {selectedRole && !selectedRole.all_access && (
                  <div className="flex gap-2">
                    {!isEditMode ? (
                      <Button onClick={handleEnterEditMode} variant="outline" size="sm">
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Permissions
                      </Button>
                    ) : (
                      <>
                        <Button 
                          onClick={handleCancelEdit} 
                          variant="outline" 
                          size="sm"
                          disabled={saving}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleSavePermissions} 
                          size="sm"
                          disabled={saving}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedRole ? (
                <div className="space-y-6">
                  {/* Save Message */}
                  {saveMessage && (
                    <div className={`p-3 rounded-md3-lg border ${
                      saveMessage.type === 'success' 
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                      <div className="flex items-center gap-2">
                        {saveMessage.type === 'success' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                        <span className="text-md3-body-small font-medium">{saveMessage.text}</span>
                      </div>
                    </div>
                  )}

                  {/* Edit Mode Banner */}
                  {isEditMode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md3-lg">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Edit2 className="h-4 w-4" />
                        <span className="text-md3-body-small font-medium">
                          Edit Mode Active - Click checkmarks/X marks to toggle permissions
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Role Info */}
                  <div className="p-3 bg-md3-surface rounded-md3-lg space-y-3">
                    <p className="text-md3-body-small font-semibold">{selectedRole.description}</p>
                    
                    {/* Default Landing Page */}
                    <div className="flex items-center gap-2 border-t pt-3">
                      <span className="text-md3-body-small font-medium text-md3-on-surface-variant">Default Landing Page:</span>
                      {isEditMode ? (
                        <select
                          value={editedDefaultPage}
                          onChange={(e) => setEditedDefaultPage(e.target.value)}
                          className="px-2 py-1 text-md3-body-small border border-md3-outline rounded-md3-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {ALL_PAGES.map(page => (
                            <option key={page} value={page}>{page}</option>
                          ))}
                        </select>
                      ) : (
                        <code className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-md3-body-small font-mono">
                          {selectedRole.default_page || '/dashboard'}
                        </code>
                      )}
                      <span className="text-xs text-md3-on-surface-variant">
                        (Users with this role will land here after login)
                      </span>
                    </div>
                    
                    <div className="flex gap-2 border-t pt-3">
                      <Badge variant="outline">
                        {ALL_PAGES.length} total pages
                      </Badge>
                      <Badge variant="outline">
                        {Object.values(selectedRole.pages || {}).filter(p => p.view).length} accessible pages
                      </Badge>
                      <Badge variant="outline">
                        {selectedRole.user_count} users
                      </Badge>
                    </div>
                  </div>

                  {/* Permissions Table */}
                  {selectedRole.all_access ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md3-lg text-center">
                      <p className="text-red-800 font-semibold">FULL SYSTEM ACCESS</p>
                      <p className="text-md3-body-small text-red-600 mt-1">
                        This role has unrestricted access to all pages and actions
                      </p>
                      <p className="text-xs text-red-500 mt-2">
                        Cannot edit this role - it always has full access
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-[500px] overflow-y-auto">
                      <Table className="bg-md3-surface-container rounded-md3-md">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Page</TableHead>
                            <TableHead className="text-center">View</TableHead>
                            <TableHead className="text-center">Create</TableHead>
                            <TableHead className="text-center">Edit</TableHead>
                            <TableHead className="text-center">Delete</TableHead>
                            <TableHead className="text-center">Export</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ALL_PAGES.map(page => {
                            const perms = isEditMode 
                              ? editedPermissions[page] 
                              : selectedRole.pages[page];
                            
                            return (
                              <TableRow key={page} className={isEditMode ? 'hover:bg-md3-surface' : ''}>
                                <TableCell className="font-mono text-xs">{page}</TableCell>
                                {['view', 'create', 'edit', 'delete', 'export'].map(action => (
                                  <TableCell 
                                    key={action}
                                    className={`text-center ${isEditMode ? 'cursor-pointer' : ''}`}
                                    onClick={() => isEditMode && handleTogglePermission(page, action)}
                                  >
                                    {perms[action] ? (
                                      <CheckCircle className={`h-4 w-4 mx-auto ${
                                        isEditMode 
                                          ? 'text-green-600 hover:text-green-700' 
                                          : 'text-green-600'
                                      }`} />
                                    ) : (
                                      <XCircle className={`h-4 w-4 mx-auto ${
                                        isEditMode 
                                          ? 'text-red-400 hover:text-red-500' 
                                          : 'text-red-400'
                                      }`} />
                                    )}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400">
                  <div className="text-center">
                    <Shield className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Select a role to view permissions</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
